import { useEffect, useMemo, useRef } from 'react'
import { useForm, UseFormWatch } from 'react-hook-form'

import { ACTION_CODE } from '@constants'
import { ActionRow, GETAllVariables, GETVariableDTO, Row } from '@types'

import {
  FieldChangeVariableContext,
  processFieldChangeVariable,
} from './processFieldChangeVariable'
import { useExecuteCommandMutation } from '@redux/api'
import { handleOpenFormAction } from '@components/DisplayForm/helpers'
import { useOpenForm } from './actions/useOpenForm'

export const useChangeVariableWithAction = (
  availableActions: ActionRow[] | undefined,
  row: Row | undefined,
  formVariables: GETVariableDTO[],
  outerVariables: GETAllVariables | undefined,
  watch: UseFormWatch<Row>,
  uniqueEventId: string
) => {
  const [executeCommand] = useExecuteCommandMutation()
  const { openForm } = useOpenForm()

  const actionForTrackingFieldNames = useMemo(
    () =>
      availableActions?.filter(
        action =>
          action.actionCode &&
          [
            ACTION_CODE.CHANGE_VARIABLE_VALUE,
            ACTION_CODE.OPEN_FORM_MODEL,
            ACTION_CODE.OPEN_ANOTHER_FORM_MODEL,
          ].includes(action.actionCode)
      ) ?? [],
    [availableActions]
  )

  const trackingFieldNames = useMemo(
    () => actionForTrackingFieldNames.map(action => action.objectFieldCode),
    [actionForTrackingFieldNames]
  )

  const runtimeRef = useRef<FieldChangeVariableContext>({
    row,
    formVariables,
    outerVariables,
    actions: availableActions ?? [],
    uniqueEventId,
    openForm,
  })

  runtimeRef.current = {
    row,
    formVariables,
    outerVariables,
    actions: availableActions ?? [],
    uniqueEventId,
    openForm,
  }

  useEffect(() => {
    if (!trackingFieldNames.length) return

    const subscription = watch((data, { name, type }) => {
      if (!name || type !== 'change') return

      const runtime = runtimeRef.current

      if (!trackingFieldNames.includes(name)) return

      processFieldChangeVariable(data, name, {
        ...runtime,
        executeCommand,
      })
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [watch, trackingFieldNames])
}
